'use client';

import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Grid3X3,
  History,
  Home,
  Layers,
  Maximize2,
  Minimize2,
  X,
} from 'lucide-react';
import React, { createContext, useCallback, useContext, useState } from 'react';

import { cn } from '@/ux/utils';

// Navigation item in history
interface NavigationItem {
  id: string;
  type: 'blog' | 'product' | 'image' | 'link';
  content: any;
  timestamp: number;
  depth: number;
  title: string;
  thumbnail?: string;
}

// Navigation context
interface NavigationContextType {
  history: NavigationItem[];
  currentIndex: number;
  push: (item: Omit<NavigationItem, 'id' | 'timestamp' | 'depth'>) => void;
  goBack: () => void;
  goForward: () => void;
  goToIndex: (index: number) => void;
  clear: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
  currentDepth: number;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

export const useInspectNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useInspectNavigation must be used within NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: React.ReactNode;
  onNavigate?: (item: NavigationItem) => void;
  maxDepth?: number;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({
  children,
  onNavigate,
  maxDepth = 5,
}) => {
  const [history, setHistory] = useState<NavigationItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const push = useCallback(
    (item: Omit<NavigationItem, 'id' | 'timestamp' | 'depth'>) => {
      const newItem: NavigationItem = {
        ...item,
        id: `nav_${Date.now()}_${Math.random()}`,
        timestamp: Date.now(),
        depth: currentIndex + 2,
      };

      setHistory((prev) => {
        // If we're not at the end, remove forward history
        const newHistory = currentIndex < prev.length - 1 ? prev.slice(0, currentIndex + 1) : prev;

        // Check max depth
        if (newHistory.length >= maxDepth) {
          // Remove oldest item
          return [...newHistory.slice(1), newItem];
        }

        return [...newHistory, newItem];
      });

      setCurrentIndex((prev) => Math.min(prev + 1, maxDepth - 1));
      onNavigate?.(newItem);
    },
    [currentIndex, maxDepth, onNavigate],
  );

  const goBack = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      onNavigate?.(history[currentIndex - 1]);
    }
  }, [currentIndex, history, onNavigate]);

  const goForward = useCallback(() => {
    if (currentIndex < history.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      onNavigate?.(history[currentIndex + 1]);
    }
  }, [currentIndex, history, onNavigate]);

  const goToIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < history.length) {
        setCurrentIndex(index);
        onNavigate?.(history[index]);
      }
    },
    [history, onNavigate],
  );

  const clear = useCallback(() => {
    setHistory([]);
    setCurrentIndex(-1);
  }, []);

  const value: NavigationContextType = {
    history,
    currentIndex,
    push,
    goBack,
    goForward,
    goToIndex,
    clear,
    canGoBack: currentIndex > 0,
    canGoForward: currentIndex < history.length - 1,
    currentDepth: currentIndex + 1,
  };

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

// Navigation Bar Component
interface NavigationBarProps {
  onClose: () => void;
  showHistoryPanel: boolean;
  onToggleHistory: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  isMobile?: boolean;
  className?: string;
}

export const NavigationBar: React.FC<NavigationBarProps> = ({
  onClose,
  showHistoryPanel,
  onToggleHistory,
  onToggleFullscreen,
  isFullscreen,
  isMobile,
  className,
}) => {
  const nav = useInspectNavigation();

  return (
    <div
      className={cn(
        'flex items-center justify-between px-4 py-2 inspect-theme-bg-elevated border-b inspect-theme-border',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        {/* Back/Forward buttons */}
        <button
          onClick={nav.goBack}
          disabled={!nav.canGoBack}
          className={cn(
            'p-2 rounded-lg transition-all',
            nav.canGoBack ? 'inspect-theme-hover cursor-pointer' : 'opacity-30 cursor-not-allowed',
          )}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <button
          onClick={nav.goForward}
          disabled={!nav.canGoForward}
          className={cn(
            'p-2 rounded-lg transition-all',
            nav.canGoForward ? 'inspect-theme-hover cursor-pointer' : 'opacity-30 cursor-not-allowed',
          )}
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Breadcrumb */}
        {nav.history.length > 0 && (
          <div className="hidden md:flex items-center gap-1 ml-2">
            <Home className="w-4 h-4 inspect-theme-text-muted" />
            {nav.history.slice(Math.max(0, nav.currentIndex - 2), nav.currentIndex + 1).map((item, idx) => (
              <React.Fragment key={item.id}>
                <ChevronRight className="w-4 h-4 inspect-theme-text-muted" />
                <button
                  onClick={() => nav.goToIndex(nav.history.indexOf(item))}
                  className={cn(
                    'px-2 py-1 rounded text-sm transition-all',
                    nav.history.indexOf(item) === nav.currentIndex
                      ? 'inspect-theme-primary font-medium'
                      : 'inspect-theme-text-muted hover:inspect-theme-text',
                  )}
                >
                  {item.title}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Depth indicator */}
        {nav.currentDepth > 0 && (
          <div className="flex items-center gap-1 ml-4 px-2 py-1 rounded-full inspect-theme-bg text-xs">
            <Layers className="w-3 h-3" />
            <span>
              {nav.currentDepth}/{nav.history.length}
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* History toggle */}
        <button
          onClick={onToggleHistory}
          className={cn(
            'p-2 rounded-lg transition-all',
            showHistoryPanel ? 'inspect-theme-primary-bg-light inspect-theme-primary' : 'inspect-theme-hover',
          )}
        >
          <History className="w-5 h-5" />
        </button>

        {/* Fullscreen toggle - desktop only */}
        {!isMobile && onToggleFullscreen && (
          <button onClick={onToggleFullscreen} className="p-2 rounded-lg inspect-theme-hover">
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
        )}

        {/* Close button */}
        <button onClick={onClose} className="p-2 rounded-lg inspect-theme-hover">
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// History Panel Component
interface HistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectItem: (item: NavigationItem) => void;
}

export const HistoryPanel: React.FC<HistoryPanelProps> = ({ isOpen, onClose, onSelectItem }) => {
  const nav = useInspectNavigation();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="absolute right-0 top-0 bottom-0 w-80 inspect-theme-bg-elevated shadow-xl z-10"
        >
          <div className="p-4 border-b inspect-theme-border">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <History className="w-5 h-5" />
                Navigation History
              </h3>
              <button onClick={onClose} className="p-1 rounded-lg inspect-theme-hover">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto p-4 space-y-2">
            {nav.history.map((item, index) => (
              <motion.button
                key={item.id}
                onClick={() => {
                  nav.goToIndex(index);
                  onSelectItem(item);
                  onClose();
                }}
                className={cn(
                  'w-full p-3 rounded-lg text-left transition-all',
                  index === nav.currentIndex
                    ? 'inspect-theme-primary-bg-light border-l-4 border-inspect-theme-primary'
                    : 'inspect-theme-hover',
                )}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-start gap-3">
                  {item.thumbnail && (
                    <img src={item.thumbnail} alt={item.title} className="w-12 h-12 rounded object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.title}</p>
                    <p className="text-sm inspect-theme-text-muted">
                      {item.type} • {new Date(item.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                  {index === nav.currentIndex && (
                    <div className="px-2 py-1 rounded text-xs inspect-theme-primary-bg-light inspect-theme-primary">
                      Current
                    </div>
                  )}
                </div>
              </motion.button>
            ))}

            {nav.history.length === 0 && (
              <div className="text-center py-8 inspect-theme-text-muted">
                <Grid3X3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No navigation history yet</p>
              </div>
            )}
          </div>

          {nav.history.length > 0 && (
            <div className="p-4 border-t inspect-theme-border">
              <button
                onClick={() => {
                  nav.clear();
                  onClose();
                }}
                className="w-full py-2 px-4 rounded-lg inspect-theme-button-secondary"
              >
                Clear History
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Transition wrapper for smooth content changes
interface ContentTransitionProps {
  children: React.ReactNode;
  contentKey: string;
}

export const ContentTransition: React.FC<ContentTransitionProps> = ({ children, contentKey }) => {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={contentKey}
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -100 }}
        transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300,
          duration: 0.3,
        }}
        className="h-full"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};
